import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../../components/layout/Layout';
import Section from '../../../components/layout/Section';
import Container from '../../../components/layout/Container';

// UI Components
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import FeatureCard from '../../../components/ui/FeatureCard';
import { FAQList } from '../../../components/ui/FAQ';
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * Manufacturing Industry Solutions Page
 */
const ManufacturingIndustryPage: React.FC = () => {
  // Filter relevant case studies for the manufacturing industry
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.industry === 'Manufacturing'
  ).slice(0, 2);

  // Filter relevant FAQs
  const relevantFaqs = faqs.slice(0, 3);

  return (
    <Layout>
      <Hero
        title="AI Solutions for Manufacturing"
        subtitle="Optimize production processes, predict maintenance needs, and reduce costs with industry-leading AI solutions for manufacturing."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Key Challenges Section */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Industry Challenges We Solve</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <h3 className="text-xl font-semibold mb-3">Equipment Downtime</h3>
                <p className="text-neutral-700 mb-4">
                  Unplanned downtime costs manufacturers billions annually. Our predictive maintenance solutions
                  help identify potential failures before they occur, reducing costly disruptions to production.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Quality Control</h3>
                <p className="text-neutral-700 mb-4">
                  Maintaining consistent product quality while scaling production is challenging. Our computer vision
                  and machine learning systems detect defects with greater accuracy than human inspection alone.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Production Efficiency</h3>
                <p className="text-neutral-700 mb-4">
                  Optimizing complex production lines with multiple variables is difficult using traditional methods.
                  Our AI solutions model and optimize production parameters for maximum throughput and efficiency.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Supply Chain Resilience</h3>
                <p className="text-neutral-700 mb-4">
                  Supply chain disruptions can halt production and impact customer deliveries. Our supply chain
                  intelligence solutions provide early warning of potential issues and recommend mitigation strategies.
                </p>
              </Card>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Our Solutions Section */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Our Manufacturing Solutions</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <FeatureCard
                title="Predictive Maintenance"
                description="Machine learning systems that analyze equipment data to predict failures up to 3 weeks in advance, reducing unplanned downtime by up to 75%."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <FeatureCard
                title="AI-Powered Quality Control"
                description="Computer vision systems that detect defects with 99.8% accuracy, significantly reducing quality issues and customer returns."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <FeatureCard
                title="Production Line Optimization"
                description="AI models that analyze production parameters to increase throughput by 15-30% while maintaining or improving quality."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <FeatureCard
                title="Energy Usage Optimization"
                description="Smart systems that analyze and adjust energy consumption patterns to reduce costs by up to 20% while maintaining production levels."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <FeatureCard
                title="Supply Chain Intelligence"
                description="AI-powered analytics that provide visibility across your supply chain, identifying risks and recommending mitigation strategies."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <FeatureCard
                title="Digital Twin Technology"
                description="Create virtual replicas of your physical assets to simulate, analyze, and optimize production processes before implementing changes."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                }
              />
            </motion.div>
            </div>

            <div className="text-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => window.location.href = '/services'}
              >
                Explore All Our Services
              </Button>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Case Studies Section */}
      {relevantCaseStudies.length > 0 && (
        <Section bgColor="bg-white" padding="lg">
          <Container>
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Success Stories in Manufacturing</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {relevantCaseStudies.map((study, index) => (
                  <motion.div
                    key={study.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex"
                  >
                    <Link to={study.url} className="w-full h-full">
                      <Card hover className="h-full flex flex-col">
                        <div className="aspect-video bg-neutral-200 rounded-t-lg overflow-hidden">
                          <img
                            src={study.imageSrc}
                            alt={study.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              if (img.nextElementSibling) {
                                (img.nextElementSibling as HTMLElement).style.display = 'flex';
                              }
                            }}
                          />
                          <div
                            className="w-full h-full bg-gradient-to-br from-primary to-secondary opacity-70 flex items-center justify-center hidden"
                            style={{ display: 'flex' }}
                          >
                            <span className="text-white text-lg font-semibold">{study.industry}</span>
                          </div>
                        </div>

                        <div className="p-6 md:p-8 flex-grow flex flex-col">
                          <div className="mb-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {study.solution}
                            </span>
                          </div>

                          <h3 className="text-xl font-semibold mb-3">{study.title}</h3>
                          <p className="text-neutral-600 mb-4 text-sm">{study.excerpt}</p>

                          <div className="mt-auto pt-2 border-t border-neutral-100">
                            <span className="inline-flex items-center text-primary font-medium text-sm">
                              Read full case study
                              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="text-center mt-10">
                <Link to="/case-studies" className="inline-flex items-center text-primary font-medium">
                  View all case studies
                  <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </Container>
        </Section>
      )}
      
      {/* FAQ Section */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQList faqs={relevantFaqs} />
            
            <div className="mt-10 text-center">
              <Link to="/contact" className="inline-flex items-center text-primary font-medium">
                Have more questions? Contact our manufacturing AI specialists
                <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Call to Action */}
      <CallToAction
        title="Ready to optimize your manufacturing operations with AI?"
        description="Schedule a consultation to discuss how our AI solutions can help your manufacturing business improve efficiency, reduce costs, and enhance quality."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default ManufacturingIndustryPage;